import React, { useEffect, useRef, useState } from "react";
import "../../styles/components/CaseArticle.css";
import Modal from "../../components/Modal";
import ContactFormSimple from "../../components/ContactFormSimple";
import arrowCircle from "../../img/hero/arrow-circle-2.png";
import cloudsImage from "../../img/cases/case-clouds-3.webp";

/* тимчасові картинки для What We Built */
import builtPhone1 from "../../img/cases/relatio-phone-1.webp";
import builtIcon1 from "../../img/cases/built-icon-9.png";
import builtPhone2 from "../../img/cases/relatio-phone-3.webp";
import builtIcon2 from "../../img/cases/built-icon-10.png";
import builtPhone3 from "../../img/cases/relatio-phone-4.webp";
import builtIcon3 from "../../img/cases/built-icon-11.png";
import builtPhone4 from "../../img/cases/relatio-phone-5.webp";
import builtIcon4 from "../../img/cases/built-icon-12.png";


import stackIcon9 from "../../img/cases/stack-icon-9.png";
import stackIcon10 from "../../img/cases/stack-icon-10.png";
import stackIcon11 from "../../img/cases/stack-icon-11.png";
import stackIcon12 from "../../img/cases/stack-icon-12.png";
import stackIcon13 from "../../img/cases/stack-icon-13.png";
import stackIcon14 from "../../img/cases/stack-icon-14.png";
import stackIcon15 from "../../img/cases/stack-icon-15.png";
import stackIcon16 from "../../img/cases/stack-icon-16.png";
import stackIcon17 from "../../img/cases/stack-icon-17.png";
import stackIcon18 from "../../img/cases/stack-icon-18.png";
import stackIcon19 from "../../img/cases/stack-icon-19.png";
import stackIcon20 from "../../img/cases/stack-icon-20.png";
import stackIcon21 from "../../img/cases/stack-icon-21.png";
import stackIcon22 from "../../img/cases/stack-icon-22.png";
import stackIcon23 from "../../img/cases/stack-icon-23.png";
import stackIcon24 from "../../img/cases/stack-icon-24.png";

import IconPng from "../../components/IconPng";

import resultsBg1 from "../../img/cases/results-bg-1.png";
import resultsBg3 from "../../img/cases/results-bg-3.png";


const sections = [
    {
        id: "about",
        title: "About the Project",
        text: [
            "Relatio is a personalized relationship coaching platform powered by a conversational AI assistant. Our task — build the AI backend that makes the assistant genuinely personal: not just answering questions, but understanding each user's journey, emotional state, and progress over time.",
        ],
    },
    {
        id: "challenge",
        title: "The Challenge",
        text: [
            "The initial solution relied on a basic ChatGPT integration with static prompts. No memory, no context, no personalization. For a coaching product — where continuity and trust are everything — this made the experience shallow and fragmented.",
            "The deeper engineering challenge: the AI needs context from multiple sources (course progress, mood tracking, saved memories, knowledge base, funnel data) to give a relevant answer — but all of this has to fit within a limited LLM context window without drowning the model in noise.",
        ],
    },
    {
        id: "goal",
        title: "The Goal",
        text: [
            "Build a system where the AI mentor genuinely knows the user — their history, progress, emotional state — and uses that knowledge to deliver coaching that feels continuous and personal. Improve retention and long-term engagement.",
        ],
    },
];

const builtCards = [
    {
        id: "built-1",
        variant: "single",
        title: "Multi-Layer Memory System",
        text:
            "Memory works as a separate personalization layer between the chat and the LLM. Before every response, the system assembles: a long-term user profile, recent context, relevant memories for the current query, and temporal context. Instead of passing all records directly, the system generates two cached summaries — persistent and recent — while specific facts are retrieved via vector search in Qdrant. This keeps cost and latency down while maintaining deep personalization.",
        icon: builtIcon1,
        image: builtPhone1,
    },

    {
        id: "built-2",
        variant: "single",
        title: "Intelligent Memory Management",
        text:
            "When the model wants to store a new fact, it goes through a dedicated decision flow: the system checks meaningfulness, duplicates, and classifies the action as new fact, update, deletion, or noop. Memories are managed records with statuses (active, superseded, deleted, inactive), so the system handles not just additions but correct updates and conflict resolution. A background cron sweep automatically deactivates temporary memories after their TTL expires and resets summaries in Redis to keep context current.",
        icon: builtIcon2,
        image: builtPhone2,
    },

    {
        id: "built-3",
        variant: "double",
        title: "Context Assembly Engine",
        text:
            "The system collects relevant context from multiple sources — course progress, mood tracker, saved memories, knowledge base content, funnel data — and assembles it intelligently for each conversation. Only the most recent user messages go into the chat context directly; everything else is shaped by the content of the current conversation. This solves the core problem: giving the model enough signal without overloading it with noise.",
        icon: builtIcon3,
        image: builtPhone3,
    },

    {
        id: "built-4",
        variant: "single",
        title: "Mood & Progress Tracking",
        text:
            "Background analysis of user emotional state and progress summarization, feeding into the memory and context layers.",
        icon: builtIcon4,
        image: builtPhone4,
    },

];

const techStack = [
    {
        title: "NestJS",
        icon: stackIcon9,
    },
    {
        title: "Prisma + PostgreSQL",
        icons: [stackIcon10, stackIcon11],
    },
    {
        title: "Qdrant",
        icon: stackIcon12,
    },
    {
        title: "Redis (latch / long-polling)",
        icon: stackIcon13,
    },
    {
        title: "Google Gemini + AI SDK",
        icon: stackIcon14,
    },
    {
        title: "embeddings pipeline",
        icon: stackIcon15,
    },
    {
        title: "RAG over app content",
        icon: stackIcon16,
    },
    {
        title: "user memory manager",
        icon: stackIcon17,
    },
    {
        title: "persona manager",
        icon: stackIcon18,
    },
    {
        title: "prompt builder",
        icon: stackIcon19,
    },
    {
        title: "tool providers",
        icon: stackIcon20,
    },
    {
        title: "content retrieval",
        icon: stackIcon21,
    },
    {
        title: "user progress summarization",
        icon: stackIcon22,
    },
    {
        title: "mood summarization",
        icon: stackIcon23,
    },
    {
        title: "content summarization",
        icon: stackIcon24,
    },
];

const resultsCards = [
    {
        variant: "small",
        icons: ["Robot-2", "Database"],
        title: "Persistent AI Coach",
        text:
            "Fully context-aware AI coaching assistant with persistent memory",
    },
    {
        variant: "soft",
        icons: ["ArrowsClockwise-2", "goyf"],
        title: "Memory Lifecycle Automation",
        text:
            "Automated memory lifecycle management (creation, updates, conflict resolution, expiration)",
        bg: resultsBg1,
    },
    {
        variant: "wide",
        icons: ["Infinity-2", "qwertyz"],
        title: "Cross-Session Continuity",
        text:
            "Continuous, coherent user experience across sessions",
    },
    {
        variant: "tall",
        icons: ["Component1", "Vector1"],
        title: "Context Assembly Engine",
        text:
            "Scalable context assembly from multiple data sources within LLM token limits",
        bg: resultsBg3,
    },
];

const beforeAfterCards = [
    {
        variant: "before",
        title: "Before",
        text:
            "Static chatbot with no memory. No awareness of user progress, mood, or history. Fragmented, impersonal experience.",
    },
    {
        variant: "after",
        title: "After",
        text:
            "AI mentor that remembers, learns, and evolves with each user. Structured memory with conflict resolution, intelligent context assembly, and personalization that deepens over time.",
    },
];

const navItems = [
    ["about", "About the Project"],
    ["challenge", "The Challenge"],
    ["goal", "The Goal"],
    ["built", "What We Built"],
    ["key", "Key Challenge"],
    ["stack", "Tech Stack"],
    ["results", "Results"],
    ["before", "Before / After"],
    ["unique", "What Makes It Unique"],
];



export default function CaseArticleRelatio() {
    const [activeId, setActiveId] = useState("about");
    const [openModal, setOpenModal] = useState(false);
    const isManualScrolling = useRef(false);
    const scrollTimer = useRef(null);

    useEffect(() => {
        const elements = navItems
            .map(([id]) => document.getElementById(id))
            .filter(Boolean);

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleEntries = entries.filter((entry) => entry.isIntersecting);

                if (visibleEntries.length > 0) {
                    if (isManualScrolling.current) return;
                    const closest = visibleEntries.sort(
                        (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
                    )[0];



                    setActiveId(closest.target.id);
                }
            },
            {
                root: null,
                rootMargin: "-25% 0px -60% 0px",
                threshold: 0,
            }
        );

        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const items = document.querySelectorAll(".case-reveal-right");

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                    }
                });
            },
            {
                threshold: 0.12,
                rootMargin: "0px 0px -80px 0px",
            }
        );

        items.forEach((item) => observer.observe(item));

        return () => observer.disconnect();
    }, []);

    const handleNavClick = (e, id) => {
        e.preventDefault();

        isManualScrolling.current = true;
        setActiveId(id);

        const section = document.getElementById(id);

        if (section) {
            section.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        clearTimeout(scrollTimer.current);

        scrollTimer.current = setTimeout(() => {
            isManualScrolling.current = false;
        }, 900);
    };

    return (
        <section className="case-article">
            <aside className="case-article__sidebar">
                <div className="case-article__toc">
                    <h3>Contents</h3>

                    <nav>
                        {navItems.map(([id, label], index) => (
                            <a
                                key={id}
                                href={`#${id}`}
                                onClick={(e) => handleNavClick(e, id)}
                                className={
                                    activeId === id
                                        ? "case-article__toc-link active"
                                        : "case-article__toc-link"
                                }
                            >
                                <span className="case-article__toc-line" />
                                <span className="case-article__toc-number">{index + 1}</span>
                                <span className="case-article__toc-label">{label}</span>
                            </a>
                        ))}
                    </nav>
                </div>

                <div className="case-article__cta">
                    <h3>
                        Ready to make your product
                        <br />
                        personal? Let’s talk!
                    </h3>

                    <button
                        type="button"
                        className="case-article__cta-btn"
                        onClick={() => setOpenModal(true)}
                    >
                        <span>Contact Us</span>
                        <img src={arrowCircle} alt="" aria-hidden="true" />
                    </button>

                    <img
                        className="case-article__cta-clouds"
                        src={cloudsImage}
                        alt=""
                        aria-hidden="true"
                    />
                </div>
            </aside>

            <div className="case-article__content">
                {sections.map((section) => (
                    <section
                        key={section.id}
                        id={section.id}
                        className="case-article__section case-reveal-right"
                    >
                        <h2>{section.title}</h2>

                        {section.text.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                    </section>
                ))}

                <section id="built" className="case-article__section">
                    <h2>What We Built</h2>

                    <div className="case-built-list">
                        {builtCards.map((card) => (
                            <article
                                className={`case-built-card case-built-card--${card.variant} case-reveal-right`}
                                key={card.id}
                            >
                                <img
                                    className="case-built-card__icon"
                                    src={card.icon}
                                    alt=""
                                    aria-hidden="true"
                                />

                                <div className="case-built-card__text">
                                    <h3>{card.title}</h3>
                                    <p>{card.text}</p>
                                </div>

                                <img
                                    className="case-built-card__image"
                                    src={card.image}
                                    alt=""
                                />
                            </article>
                        ))}
                    </div>
                </section>
                <section id="key" className="case-article__section case-reveal-right">
                    <h2>Key Challenge</h2>

                    <p>
                        Fitting a rich user context into a limited LLM window. The AI needs to know the user's course
                        progress, emotional trajectory, past conversations, relevant coaching content, and funnel
                        stage — all at once. Passing everything raw would blow the context window and drown the model
                        in irrelevant data.
                    </p>

                    <p>
                        The solution: a layered context architecture. Cached summaries handle the broad picture
                        (persistent profile + recent state), vector search pulls in specific relevant memories,
                        and the context assembly logic decides what matters for this particular conversation.
                        The result — the model gets a compact but comprehensive view of the user, every time.
                    </p>
                </section>
                <section id="stack" className="case-article__section case-reveal-right">
                    <div className="case-stack">

                        <h2 className="case-stack__title">Tech Stack</h2>

                        <div className="case-stack__grid">
                            {techStack.map((item) => (
                                <div
                                    key={item.title}
                                    className={`case-stack__card ${item.size || ""}`}
                                >
                                    <div className="case-stack__icons">
                                        {item.icons
                                            ? item.icons.map((icon, index) => (
                                                <img key={index} src={icon} alt="" />
                                            ))
                                            : (
                                                <img
                                                    src={item.icon}
                                                    alt=""
                                                    className={item.iconSize || ""}
                                                />
                                            )}
                                    </div>

                                    <span>{item.title}</span>
                                </div>
                            ))}
                        </div>

                    </div>
                </section>

                <section id="results" className="case-article__section case-reveal-right">
                    <h2>Results</h2>

                    <div className="case-results">
                        {resultsCards.map((card, index) => (
                            <article
                                key={index}
                                className={`case-results__card case-results__card--${card.variant}`}
                            >
                                {card.bg && (
                                    <img
                                        className="case-results__bg"
                                        src={card.bg}
                                        alt=""
                                        aria-hidden="true"
                                    />
                                )}

                                <div className="case-results__icons">
                                    {card.icons.map((icon) => (
                                        <span className="case-results__icon" key={icon}>
                                            <IconPng name={icon} size={28} />
                                        </span>
                                    ))}
                                </div>

                                {card.title && <h3>{card.title}</h3>}
                                {card.text && <p>{card.text}</p>}
                            </article>
                        ))}
                    </div>
                </section>

                <section id="before" className="case-article__section case-reveal-right">
                    <div className="case-before-after">
                        {beforeAfterCards.map((card) => (
                            <article
                                key={card.variant}
                                className={`case-before-after__card case-before-after__card--${card.variant}`}
                            >
                                <h2>{card.title}</h2>
                                <p>{card.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section id="unique" className="case-article__section case-reveal-right">
                    <h2>What Makes It Unique</h2>

                    <p>
                        Not a chatbot with a prompt — a full memory and context architecture built
                        for long-term personal relationships at scale. The multi-layer memory system
                        with automated lifecycle management, the intelligent context assembly that
                        fits rich user data into LLM constraints, and the content variation handling
                        create an AI that behaves like a real mentor: it remembers what matters,
                        forgets what doesn’t, and gets better with every conversation.
                    </p>
                </section>

            </div>
            <Modal
                open={openModal}
                onClose={() => setOpenModal(false)}
                title="Start your project"
            >
                <ContactFormSimple onSubmit={() => setOpenModal(false)} />
            </Modal>
        </section>
    );
}
